<template>

  <div class="w-full">

    <Dialog v-model:visible="showEditDialog"
            :draggable="false"
            modal
            :header="`Aktualizovať PLU - ${restaurantPlace}`"
            position="top"
            :style="{ width: '100%','max-width':'80%' }"
    >

      <div class="text-center font-bold">
        Odstránene záznamy z PLU
      </div>

      <DataTable v-if="updateState.removedItems.length !== 0" :value="updateState.removedItems" tableStyle="width:100%">
        <Column field="pluCode" header="Plu Code"></Column>
        <Column field="pluName" header="Názov"></Column>
        <Column field="pluPrice" header="Cena">
          <template #body="slotProps">
            {{ Number(slotProps.data.pluPrice).toFixed(2) }} €
          </template>
        </Column>
      </DataTable>
      <div v-else class="text-center">
        Nenašli sa žiadne odstránené záznamy
      </div>

      <div class=" mt-4 text-center font-bold">
        Nové záznamy v PLU
      </div>

      <DataTable v-if="updateState.addedItems.length !== 0" :value="updateState.addedItems" tableStyle="width:100%">
        <Column field="pluCode" header="Plu Code"></Column>
        <Column field="pluName" header="Názov"></Column>
        <Column field="pluPrice" header="Cena">
          <template #body="slotProps">
            {{ Number(slotProps.data.pluPrice).toFixed(2) }} €
          </template>
        </Column>
        <Column field="pluCategoryName" header="Plu kategória"></Column>
      </DataTable>
      <div v-else class="text-center">
        Nenašli sa žiadne zmeny
      </div>

      <div class=" mt-4 text-center font-bold">
        Aktualizované záznamy v PLU
      </div>

      <DataTable v-if="updateState.updatedItems.length !== 0" :value="updateState.updatedItems" tableStyle="width:100%">
        <Column field="old.pluCode" header="Plu Code"></Column>
        <Column field="old.pluName" header="Pôvodny názov"></Column>
        <Column field="new.pluName" header="Nový názov"></Column>
        <Column field="old.pluPrice" header="Pôvodna Cena">
          <template #body="slotProps">
            {{ Number(slotProps.data.old.pluPrice).toFixed(2) }} €
          </template>
        </Column>
        <Column field="new.pluPrice" header="Nová Cena">
          <template #body="slotProps">
            {{ Number(slotProps.data.new.pluPrice).toFixed(2) }} €
          </template>
        </Column>
      </DataTable>
      <div v-else class="text-center">
        Nenašli sa žiadne zmeny
      </div>

      <!--  footer -->
      <hr class="my-4">

      <div class="flex space-x-2 justify-end">
        <Button type="button"
                label="Zušiť"
                :disabled="updating"
                size="small"
                severity="secondary"
                @click="closeUpdate"
        />
        <Button @click="executeUpdate"
                :disabled="!canUpdate"
                :loading="updating"
                size="small" label="Uložiť"
                icon="pi pi-check"
                iconPos="right"
        />
      </div>

    </Dialog>

    <div class="flex justify-between w-full">

      <div class="">
        <SelectButton v-model="restaurantPlace"
                      :options="RESTAURANT_PLACES"
                      aria-labelledby="basic"
                      size="small"
                      option-value="value"
                      optionLabel="name"
                      :disabled="loading"
        />
      </div>
      <Button @click="updatePlu" :loading="loading" size="small" label="Aktualizovať udaje z PLU" icon="pi pi-refresh"
              iconPos="right"/>
    </div>

    <hr class="my-4">

    <InputText v-model="search" type="text" size="small" placeholder="Hladanie"/>

    <DataTable :value="filteredPluItems" tableStyle="width:100%">
      <Column field="pluName" header="Názov"></Column>
      <Column field="pluCode" header="Plu Code"></Column>
      <Column field="pluId" header="Plu Id"></Column>
      <Column field="pluPrice" header="Plu cena">
        <template #body="slotProps">
          {{ Number(slotProps.data.pluPrice).toFixed(2) }} €
        </template>
      </Column>
      <Column field="value" header="App id"></Column>
    </DataTable>

  </div>

</template>

<script setup>
import {RESTAURANT_PLACES} from "@/content/lists.js";
import {computed, reactive, ref} from 'vue'
import usePluStore from "@/stores/pluStore.js";
import Supabase from "@/utils/Supabase";
import {useToast} from "primevue/usetoast";

const pluStore = usePluStore()
const toast = useToast();

const restaurantPlace = ref('BO')
const search = ref('')
const selectedRestaurantPluItems = computed(() => restaurantPlace.value === 'BO' ? pluStore.pluItemsBo : pluStore.pluItemsPE)
const filteredPluItems = computed(() => selectedRestaurantPluItems.value.filter(pluItem => pluItem.pluName.toLowerCase().includes(search.value.toLowerCase())))

const loading = ref(false)


/*
 * Update state / function
 */
const updating = ref(false)
const showEditDialog = ref(false)

const updateState = reactive({
  removedItems: [],
  addedItems: [],
  updatedItems: [],
  meta: {
    deleteIdList: [],
  }
})
const canUpdate = computed(() => !loading.value && !updating.value && (updateState.removedItems.length !== 0 || updateState.addedItems.length !== 0 || updateState.updatedItems.length !== 0))

const closeUpdate = () => {
  showEditDialog.value = false
  updateState.removedItems = []
  updateState.addedItems = []
  updateState.updatedItems = []
}

const updatePlu = async () => {
  updateState.removedItems = []
  updateState.addedItems = []
  updateState.updatedItems = []
  loading.value = true
  fetch(`${import.meta.env.VITE_API_URL}/update-plu-list`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      place: restaurantPlace.value
    })
  }).then(async response => {
    console.log('response', response)
    return await response.json()
  }).then(async data => {
    console.log('Success:', data);
    if (data.success) {

      const MASTER_PAIR_KEY = 'pluCode'

      const mappedOriginalPluItems = {}
      selectedRestaurantPluItems.value.forEach(pluItem => {
        mappedOriginalPluItems[pluItem[MASTER_PAIR_KEY]] = {
          ...pluItem
        }
      })
      const originalPluItemsCodes = Object.keys(mappedOriginalPluItems).map(pluCode => Number(pluCode))

      const responseItemsFromPlu = data.data || []
      if (responseItemsFromPlu.length === 0) {
        alert('Neboli nájdené žiadne položky')
        return
      }

      const mappedNewPluItems = {}
      responseItemsFromPlu.forEach(pluItem => {
        mappedNewPluItems[pluItem[MASTER_PAIR_KEY]] = {
          ...pluItem
        }
      })
      const newPluItemsCodes = Object.keys(mappedNewPluItems).map(pluCode => Number(pluCode))

      updateState.removedItems = selectedRestaurantPluItems.value.filter(pluItem => !newPluItemsCodes.includes(pluItem[MASTER_PAIR_KEY]))
      if (updateState.removedItems.length !== 0) {
        // console.log('updateState.removedItems',updateState.removedItems)
        // updateState.meta.deleteIdList = updateState.removedItems.map(pluItem => pluItem.pluId)
        updateState.meta.deleteIdList = updateState.removedItems.map(pluItem => pluItem.value)
      }
      updateState.addedItems = responseItemsFromPlu.filter(pluItem => !originalPluItemsCodes.includes(pluItem[MASTER_PAIR_KEY]))
      //
      // /*
      //  * compare name and price
      //  * return as {old:{...},new:{...}}
      //  */
      updateState.updatedItems = responseItemsFromPlu.filter(pluItem => {
        const originalPluItem = mappedOriginalPluItems[pluItem[MASTER_PAIR_KEY]]
        if (!originalPluItem) return false
        return originalPluItem.pluName !== pluItem.pluName || originalPluItem.pluPrice !== pluItem.pluPrice
      }).map(pluItem => {
        return {
          old: mappedOriginalPluItems[pluItem[MASTER_PAIR_KEY]],
          new: pluItem
        }
      })

      if (updateState.removedItems.length === 0 && updateState.addedItems.length === 0 && updateState.updatedItems.length === 0) {
        alert('Zoznam je aktuálny')
        return
      }

      await pluStore.loadPluItems()

      showEditDialog.value = true
      loading.value = false

    } else {
      alert('Došlo k chybe')
    }
  }).catch((error) => {
    console.error('Error:', error);
    alert('Došlo k chybe')
  }).finally(() => {
    loading.value = false
  })

}

const executeUpdate = async () => {
  if (!window.confirm('Si si istý ? Tento proces je nevratný')) return
  updating.value = true

  console.log('executeUpdate()')
  console.log('updateState.removedItems', updateState.removedItems)
  console.log('updateState.meta.deleteIdList', updateState.meta.deleteIdList)
  // return

  if (updateState.removedItems.length !== 0) {
    // const mapped = updateState.meta.deleteIdList.filter(id => !!id)
    const {error} = await Supabase.from('v2_Hajcer_PluItems').delete().in('id', updateState.meta.deleteIdList)
    if (error) {
      toast.add({severity: 'error', summary: 'Rejected', detail: 'Error deleting old PLU items', life: 3000});
    } else {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Old PLU items deleted - ' + updateState.meta.deleteIdList.length,
        life: 3000
      });
    }
  }

  if (updateState.addedItems.length !== 0) {
    const mapNewItems = updateState.addedItems.map(item => {
      return {
        pluId: item.pluId,
        pluCode: item.pluCode,
        pluName: item.pluName,
        pluPrice: item.pluPrice,
        pluCategoryName: item.pluCategoryName,
        active: true,
        restaurantPlace: restaurantPlace.value
      }
    })

    const {error} = await Supabase.from('v2_Hajcer_PluItems').insert(mapNewItems)

    if (error) {
      console.error('2 Error:', error);
      console.log('mapNewItems', mapNewItems)
      toast.add({severity: 'error', summary: 'Rejected', detail: 'Error inserting new PLU items', life: 3000});
    } else {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'New PLU items added - ' + updateState.addedItems.length,
        life: 3000
      });
    }
  }

  if (updateState.updatedItems.length !== 0) {

    // console.log('????', updateState.updatedItems)
    // return
    const mapUpdatedItems = updateState.updatedItems.map(item => {
      return {
        id: item.old.value,
        pluId: item.new.pluId,
        pluCode: item.new.pluCode,
        pluName: item.new.pluName,
        pluPrice: item.new.pluPrice,
        pluCategoryName: item.new.pluCategoryName,
        active: true,
        restaurantPlace: restaurantPlace.value
      }
    })

    const {error} = await Supabase.from('v2_Hajcer_PluItems').upsert(mapUpdatedItems)

    if (error) {
      console.error('1 Error:', error);
      console.log('mapUpdatedItems', mapUpdatedItems)
      toast.add({severity: 'error', summary: 'Rejected', detail: 'Error updating PLU items', life: 3000});
    } else {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'PLU items updated - ' + updateState.updatedItems.length,
        life: 3000
      });
    }
  }

  updating.value = false
  closeUpdate()
  window.location.reload()


}

</script>